import { ImagePlus } from 'lucide-react'
import { SettingsSheet } from '@/app/settings/SettingsSheet'
import { useEditSession } from '@/core/store/editSession'
import { Button } from '@/ui/shadcn/button'
import { TEST_IDS } from '@/lib/testIds'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/shadcn/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/shadcn/tooltip'
import { UndoRedoButtons } from '@/ui/UndoRedoButtons'
import { getUtility } from '@/tools/registry'
import { UtilityId } from '@/tools/types'

const previewUtility = getUtility(UtilityId.Preview)

interface TopBarProps {
  mobile: boolean
  onChooseImage: () => void
}

export function TopBar({ mobile, onChooseImage }: TopBarProps) {
  const document = useEditSession((state) => state.document)
  const setPreviewOpen = useEditSession((state) => state.setPreviewOpen)
  const PreviewIcon = previewUtility.icon
  const PreviewOverlay = previewUtility.Overlay

  const newImageButton = (
    <Button type="button" variant="ghost" size={mobile ? 'icon' : 'sm'} onClick={document ? undefined : onChooseImage}>
      <ImagePlus aria-hidden="true" />
      {mobile ? <span className="sr-only">New image</span> : 'New image'}
    </Button>
  )

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b bg-background/95 px-3 backdrop-blur md:px-5">
      <div className="flex min-w-0 items-center gap-2">
        <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-foreground text-sm font-black text-background">I</div>
        <span className="truncate text-sm font-semibold tracking-tight sm:text-base">Insta Little Helper</span>
      </div>
      <div className="flex items-center gap-1" data-testid={TEST_IDS.topBarActions}>
        {document ? (
          <Dialog>
            <DialogTrigger asChild>{newImageButton}</DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start with a new image?</DialogTitle>
                <DialogDescription>Your current crop settings will be cleared. Export first if you want to keep the result.</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2">
                <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
                <DialogClose asChild><Button type="button" onClick={onChooseImage}>Choose image</Button></DialogClose>
              </div>
            </DialogContent>
          </Dialog>
        ) : newImageButton}

        <UndoRedoButtons />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={!document}
              onClick={() => setPreviewOpen(true)}
              aria-label="Open Instagram preview"
              data-testid={TEST_IDS.previewOpen}
            >
              <PreviewIcon aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Preview on Instagram</TooltipContent>
        </Tooltip>
        <SettingsSheet mobile={mobile} />
      </div>
      {document ? <PreviewOverlay /> : null}
    </header>
  )
}
